import React from 'react';
import {
  Box, Heading, HStack, Icon, Pressable, ScrollView, Text, VStack,
} from '@gluestack-ui/themed';
import { ArrowLeft } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import type { ScheduledChangeDetail } from '@/common/api/models';
import { ScheduledChangeStateBanner } from './components/ScheduledChangeStateBanner';
import { ScheduledChangeStateChip } from './components/ScheduledChangeStateChip';
import { scheduledChangeViews } from './views';

interface Props {
  change: ScheduledChangeDetail;
  showConflictBanner?: boolean;
  onDismissConflict?: () => void;
  onBack: () => void;
  onOpenVersion: (moduleId: string, version: string) => void;
  onSwap: (questionId: string) => void;
}

export const ScheduledChangesDetail: React.FC<Props> = ({
  change,
  showConflictBanner = false,
  onDismissConflict,
  onBack,
  onOpenVersion,
  onSwap,
}) => {
  const { t } = useTranslation();
  const view = scheduledChangeViews[change.kind as keyof typeof scheduledChangeViews]
    ?? scheduledChangeViews.oral_exam_question_update;
  const ReviewBody = change.kind === 'working_mode_switch'
    ? (
      <view.Review
        change={change}
        onOpenDraft={(moduleId, version) => onOpenVersion(moduleId, version)}
      />
    )
    : (
      <view.Review change={change} onSwap={onSwap} />
    );

  return (
    <VStack flex={1}>
      <HStack px="$6" py="$4" alignItems="center" space="md" borderBottomWidth={1} borderColor="$borderLight">
        <Pressable onPress={onBack} p="$1" testID="scheduled-changes-back">
          <Icon as={ArrowLeft} size="md" color="$textSubtle" />
        </Pressable>
        <VStack flex={1}>
          <Heading size="md" numberOfLines={1}>
            {change.moduleTitle}
          </Heading>
          <Text size="sm" color="$textSubtle">
            {t('lecturer.scheduledChanges.version', { version: change.version })}
          </Text>
        </VStack>
        <ScheduledChangeStateChip
          state={change.state}
          kind={change.kind}
          standDownReason={change.workingMode?.reason}
        />
      </HStack>
      <ScrollView
        flex={1}
        px="$6"
        py="$4"
        contentContainerStyle={{ paddingBottom: 24 }}
      >
        <VStack space="md">
          <ScheduledChangeStateBanner
            change={change}
            onOpenVersion={(version) => onOpenVersion(change.moduleId, version)}
          />
          {!!showConflictBanner && (
            <Box
              bg="$statusWarningSubtle"
              borderLeftWidth={3}
              borderLeftColor="$statusWarning"
              borderRadius="$sm"
              px="$3.5"
              py="$3"
              testID="scheduled-change-conflict-banner"
            >
              <HStack alignItems="flex-start" justifyContent="space-between" space="md">
                <VStack flex={1} space="xs">
                  <Text size="sm" fontFamily="$heading" color="$textOnWarningSubtle">
                    {t('lecturer.scheduledChanges.conflict')}
                  </Text>
                  <Text size="sm" color="$textOnWarningSubtle">
                    {t('lecturer.scheduledChanges.conflictHint')}
                  </Text>
                </VStack>
                {onDismissConflict ? (
                  <Pressable onPress={onDismissConflict} p="$1" testID="scheduled-change-conflict-dismiss">
                    <Text size="sm" color="$textOnWarningSubtle">
                      ×
                    </Text>
                  </Pressable>
                ) : null}
              </HStack>
            </Box>
          )}
          {ReviewBody}
        </VStack>
      </ScrollView>
    </VStack>
  );
};
